import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Formik } from 'formik';
import { useToasts } from 'react-toast-notifications';
import * as Yup from 'yup';
import {
  FiAlertCircle,
  FiCheckCircle,
  FiInfo,
  FiMail,
  FiShield,
  FiTrash2,
  FiUser,
} from 'react-icons/fi';

import api from '../../services/api';

import logoHorizontal from '../../assets/logo/1x/logoHorizontal.png';
import logoHorizontalWhite from '../../assets/logo/1x/logoHorizontalWhite.png';

import {
  BrandBlock,
  Field,
  FormBlock,
  FormWrap,
  Helper,
  HeroBullets,
  HeroContent,
  InfoCard,
  InputWrapper,
  LeftPanel,
  PageContainer,
  RightPanel,
  SubmitButton,
} from './styles';

interface IDataDeletionRequest {
  name: string;
  email: string;
  reason: string;
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Nome é obrigatório'),
  email: Yup.string().email('E-mail inválido').required('E-mail é obrigatório'),
  reason: Yup.string().required('Motivo é obrigatório'),
});

const DataDeletionRequest: React.FC = () => {
  const { addToast } = useToasts();
  const formRef = useRef<HTMLFormElement | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = 'Exclusão de Dados - Viva Voz';
  }, []);

  const handleSubmitRequest = useCallback(
    async (data: IDataDeletionRequest) => {
      setLoading(true);
      try {
        await api.post('/users/forgot', data.email);
        addToast('Solicitação enviada com sucesso!', {
          appearance: 'success',
          autoDismiss: true,
        });
      } catch (err) {
        addToast('Erro ao enviar solicitação. Tente novamente.', {
          appearance: 'error',
          autoDismiss: true,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  return (
    <PageContainer>
      <LeftPanel>
        <FormWrap>
          <BrandBlock $delay={50}>
            <img src={logoHorizontal} alt="Viva Voz" />
            <h1>Solicitação de Exclusão de Dados</h1>
            <p>
              Este formulário é destinado a usuários do aplicativo Viva Voz que
              desejam solicitar a exclusão de seus dados pessoais.
            </p>
          </BrandBlock>

          <InfoCard $delay={150}>
            <span className="heading">
              <FiInfo size={14} />
              Como funciona
            </span>
            <ul>
              <li>Preencha seu nome, e-mail e o motivo da solicitação.</li>
              <li>Você receberá uma confirmação por e-mail.</li>
              <li>
                Os dados serão excluídos em até
                {' '}
                <strong>15 dias úteis</strong>
                , salvo obrigação legal.
              </li>
            </ul>
          </InfoCard>

          <InfoCard $delay={220}>
            <span className="heading">
              <FiShield size={14} />
              Dados que serão excluídos
            </span>
            <p>
              Nome completo, e-mail, conteúdos inseridos no app (relatórios,
              comentários, avaliações, respostas) e dados de uso vinculados ao
              seu perfil.
            </p>
            <p>
              <strong>Podem ser mantidos:</strong>
              {' '}
              apenas informações
              obrigatórias por lei, por tempo limitado.
            </p>
          </InfoCard>

          <Formik
            initialValues={{ name: '', email: '', reason: '' }}
            onSubmit={values => handleSubmitRequest(values)}
            validationSchema={validationSchema}
          >
            {({
              values,
              errors,
              touched,
              handleBlur,
              handleChange,
              handleSubmit,
            }) => (
              <FormBlock ref={formRef} onSubmit={handleSubmit} $delay={300}>
                <Field>
                  <label htmlFor="name">
                    Nome completo
                    <InputWrapper hasError={touched.name && !!errors.name}>
                      <FiUser className="input-icon" />
                      <input
                        id="name"
                        type="text"
                        placeholder="Seu nome"
                        onChange={handleChange('name')}
                        onBlur={handleBlur('name')}
                        value={values.name}
                      />
                    </InputWrapper>
                  </label>
                  {touched.name && errors.name && (
                    <span className="error">{errors.name}</span>
                  )}
                </Field>

                <Field>
                  <label htmlFor="email">
                    E-mail
                    <InputWrapper hasError={touched.email && !!errors.email}>
                      <FiMail className="input-icon" />
                      <input
                        id="email"
                        type="email"
                        placeholder="seu@email.com"
                        onChange={handleChange('email')}
                        onBlur={handleBlur('email')}
                        value={values.email}
                      />
                    </InputWrapper>
                  </label>
                  {touched.email && errors.email && (
                    <span className="error">{errors.email}</span>
                  )}
                </Field>

                <Field>
                  <label htmlFor="reason">
                    Motivo da solicitação
                    <InputWrapper hasError={touched.reason && !!errors.reason}>
                      <FiAlertCircle className="input-icon" />
                      <textarea
                        id="reason"
                        rows={4}
                        placeholder="Descreva o motivo da exclusão"
                        onChange={handleChange('reason')}
                        onBlur={handleBlur('reason')}
                        value={values.reason}
                      />
                    </InputWrapper>
                  </label>
                  {touched.reason && errors.reason && (
                    <span className="error">{errors.reason}</span>
                  )}
                </Field>

                <SubmitButton type="submit" disabled={loading}>
                  {loading ? (
                    <span className="spinner" />
                  ) : (
                    <>
                      <FiTrash2 size={16} />
                      Solicitar exclusão
                    </>
                  )}
                </SubmitButton>
              </FormBlock>
            )}
          </Formik>

          <Helper $delay={400}>
            Ficou em dúvida? Fale com o suporte do seu sindicato.
          </Helper>
        </FormWrap>
      </LeftPanel>

      <RightPanel $delay={100}>
        <div className="pattern" />
        <HeroContent>
          <img src={logoHorizontalWhite} alt="Viva Voz" />
          <h2>Privacidade e controle dos seus dados</h2>
          <p>
            Conforme a LGPD, você pode solicitar a exclusão de seus dados
            pessoais a qualquer momento. Garantimos transparência em cada etapa
            do processo.
          </p>
        </HeroContent>

        <HeroBullets>
          <li>
            <FiCheckCircle size={16} />
            Resposta por e-mail em até 15 dias úteis
          </li>
          <li>
            <FiCheckCircle size={16} />
            Exclusão definitiva dos dados pessoais
          </li>
          <li>
            <FiCheckCircle size={16} />
            Anonimização do conteúdo associado ao perfil
          </li>
        </HeroBullets>
      </RightPanel>
    </PageContainer>
  );
};

export default DataDeletionRequest;
